import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAction, useApi, useRead } from './context';
import { Confirm, Empty, Failure, Loading, Section, When } from './bits';
import { BrandingPreview } from './BrandingPreview';
import type {
  BrandingDocument,
  BrandingRead,
  BrandingSection,
  ContractSection,
  ContractSlot,
  ContractToken,
  ThemeContract,
  ZoneReport,
} from '../wire';

/**
 * The theme editor (spec §6.4.3) — the second of the three rows a tenant's
 * website is, after `tenant_domains`.
 *
 * "Branding lives in rows, so the admin console gets a **theme editor with live
 * preview** against the tenant's real catalog, publish bumps a
 * `branding_version`, and rollback is restoring a row — no deploys anywhere on
 * rungs 1–4."
 *
 * ## Everything here is rendered from the server's vocabulary
 *
 * There is no token table in this file, no list of presets and no list of
 * blocks. `GET /branding` sends the contract — token names with their types,
 * labels and groups; the three presets with their whole token sets; the block
 * registry with its slot shapes; the sanitizer's allowlist; every limit — and
 * the controls below are built from it. A service that learns a new token is a
 * console that renders a new input on the next page load.
 *
 * That is the same discipline the modules card follows for a different reason.
 * There, the point is that the server owns every refusal; here, the point is
 * that the *site bundle* owns the vocabulary, and a copy of it in a React
 * component would be the third one (see `apps/admin-api/src/branding/contract.ts`
 * for the second, and for why it is temporary).
 *
 * ## What the editor does not decide
 *
 * The server validates and the console renders the refusal. This screen does not
 * pre-check a colour, does not grey out an unknown token and does not sanitize:
 * **it shows what came back**. After a publish the textareas hold the *stored*
 * bytes rather than what was typed, and the report says what was taken out — so
 * an owner who pasted a styled Word document learns the rules by watching them
 * apply to their own words, in the same second, instead of reading a list.
 *
 * `booking` is pinned rather than offered as a checkbox: the bundle mounts the
 * booking flow whether or not the block is arranged, so a control to exclude it
 * would not mean what it says.
 */

interface SectionRow {
  type: string;
  include: boolean;
  slots: Record<string, unknown>;
}

interface Draft {
  preset: string;
  tokens: Record<string, string>;
  rows: SectionRow[];
  site_title: string;
  tagline: string;
  custom_css: string;
}

export function TenantBranding({ tenantId }: { tenantId: string }): ReactNode {
  const api = useApi();
  const page = useRead(() => api.branding(tenantId), [tenantId]);

  const publish = useAction();
  const rollback = useAction();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [rollingBackTo, setRollingBackTo] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [zones, setZones] = useState<ZoneReport[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  // The draft is seeded from the server's answer and re-seeded after every
  // write, so what is on screen is always something the server said — the same
  // "nothing is optimistic" rule the rest of the console follows.
  useEffect(() => {
    if (page.value !== null) setDraft(fromDocument(page.value.branding, page.value.contract));
  }, [page.value]);

  const contract = page.value?.contract ?? null;

  // Named `pending` rather than `document`: this file runs in a browser and
  // shadowing the global `document` inside a React component is a trap for
  // whoever adds a DOM call to it later.
  const pending = useMemo(() => (draft === null ? null : toDocument(draft)), [draft]);

  const doPublish = async (): Promise<void> => {
    if (pending === null) return;
    const ok = await publish.run(async () => {
      const answer = await api.publishBranding(tenantId, pending, note);
      setResult(
        `Published version ${String(answer.branding_version)} (${String(answer.document_bytes)} bytes). ${answer.effective}`,
      );
      setZones(answer.zones);
      setWarnings(answer.warnings);
      // The stored bytes replace the typed ones. This is the teaching step.
      if (contract !== null) setDraft(fromDocument(answer.branding, contract));
      setNote('');
    });
    if (ok) {
      setConfirming(false);
      page.reload();
    }
  };

  const doRollback = async (): Promise<void> => {
    if (rollingBackTo === null) return;
    const ok = await rollback.run(async () => {
      const answer = await api.rollbackBranding(tenantId, rollingBackTo);
      setResult(
        `Restored version ${String(answer.restored_from)} as version ${String(answer.branding_version)}. ${answer.effective}`,
      );
      setZones([]);
      setWarnings([]);
      if (contract !== null) setDraft(fromDocument(answer.branding, contract));
    });
    if (ok) {
      setRollingBackTo(null);
      page.reload();
    }
  };

  return (
    <Section
      title="Branding"
      note="Rungs 1–4 of the customization ladder: a preset, their tokens, the section order, and their own words. The shared bundle is never edited for one tenant (spec §6.4.3)."
      actions={
        <div className="row-actions">
          <button
            type="button"
            className="button"
            onClick={page.reload}
            disabled={page.loading || publish.busy}
          >
            Discard edits
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              publish.clear();
              setConfirming(true);
            }}
            disabled={draft === null || publish.busy}
          >
            Publish
          </button>
        </div>
      }
    >
      {page.failure !== null ? <Failure failure={page.failure} /> : null}
      {!confirming && publish.failure !== null ? <Failure failure={publish.failure} /> : null}
      {rollingBackTo === null && rollback.failure !== null ? (
        <Failure failure={rollback.failure} />
      ) : null}
      {result !== null ? <p className="ok">{result}</p> : null}
      {warnings.map((warning) => (
        <p className="warn" key={warning}>
          {warning}
        </p>
      ))}

      {page.value === null || draft === null || contract === null ? (
        page.loading ? (
          <Loading what="the tenant’s theme" />
        ) : null
      ) : (
        <>
          <Published read={page.value} onRollback={setRollingBackTo} />

          <label className="field field--wide">
            <span className="field__label">Publish note</span>
            <input
              aria-label="Publish note"
              value={note}
              placeholder="why this change"
              onChange={(e) => setNote(e.target.value)}
            />
            <span className="field__hint">
              Stored on the version and written into the audit row, so the trail answers “why did
              this site change on the 14th” without anyone having to remember.
            </span>
          </label>

          <div className="branding">
            <div className="branding__editor">
              <Presets
                contract={contract}
                value={draft.preset}
                onPick={(preset) => setDraft({ ...draft, preset })}
              />

              <Identity
                draft={draft}
                fallbackName={page.value.preview.name}
                onChange={(patch) => setDraft({ ...draft, ...patch })}
              />

              <Tokens
                contract={contract}
                preset={draft.preset}
                overrides={draft.tokens}
                onChange={(tokens) => setDraft({ ...draft, tokens })}
              />

              <Arrangement
                contract={contract}
                rows={draft.rows}
                zones={zones}
                onChange={(rows) => setDraft({ ...draft, rows })}
              />

              <CustomCss
                contract={contract}
                value={draft.custom_css}
                pinnedTo={page.value.custom_css_contract_version}
                onChange={(custom_css) => setDraft({ ...draft, custom_css })}
              />

              <Sanitizer contract={contract} zones={zones} />
            </div>

            <aside className="branding__preview">
              <h4>Preview</h4>
              <p className="muted">
                A sketch, not the bundle: the tokens are real CSS custom properties and the name and
                week are this tenant’s, but <code>apps/site</code> is the authoritative renderer and
                the composition here only stands in for it.
              </p>
              <BrandingPreview
                name={page.value.preview.name}
                preset={draft.preset}
                presets={contract.presets}
                overrides={draft.tokens}
                sections={included(draft)}
                hours={page.value.preview.hours}
                siteTitle={draft.site_title}
                tagline={draft.tagline}
              />
              <p className="field__hint">{page.value.preview.catalog_note}</p>
            </aside>
          </div>
        </>
      )}

      {confirming ? (
        <Confirm
          title={`Publish this theme to ${tenantId}?`}
          consequence={
            'Every guest sees it on their next page load. guest-api reads this row on each ' +
            'request to /api/site, so there is no deploy and no build to wait for — the site ' +
            'changes the moment this is written. Content zones are sanitized on the way in and ' +
            'only the sanitized bytes are stored; the editor will show you what survived.'
          }
          confirmLabel="Publish"
          busy={publish.busy}
          failure={publish.failure}
          onConfirm={() => void doPublish()}
          onCancel={() => {
            publish.clear();
            setConfirming(false);
          }}
        />
      ) : null}

      {rollingBackTo !== null ? (
        <Confirm
          title={`Restore version ${String(rollingBackTo)}?`}
          consequence={
            `Version ${String(rollingBackTo)}’s document is published again, as a new and higher ` +
            'version — the number moves forward, because a rollback is a publish of older bytes ' +
            'and not a rewind. Every guest sees it on their next page load.'
          }
          confirmLabel={`Restore version ${String(rollingBackTo)}`}
          busy={rollback.busy}
          failure={rollback.failure}
          onConfirm={() => void doRollback()}
          onCancel={() => {
            rollback.clear();
            setRollingBackTo(null);
          }}
        />
      ) : null}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* What is published now, and what can be restored                     */
/* ------------------------------------------------------------------ */

function Published({
  read,
  onRollback,
}: {
  read: BrandingRead;
  onRollback: (version: number) => void;
}): ReactNode {
  return (
    <div className="branding__published">
      <dl className="facts">
        <div>
          <dt>Version</dt>
          <dd>
            {read.branding_version === 0 ? (
              <span className="muted">never published</span>
            ) : (
              read.branding_version
            )}
          </dd>
        </div>
        <div>
          <dt>Published</dt>
          <dd>
            <When iso={read.published_at} />
          </dd>
        </div>
        <div>
          <dt>By</dt>
          <dd>{read.published_by ?? <span className="muted">—</span>}</dd>
        </div>
        <div>
          <dt>Theme contract</dt>
          <dd>
            <code>v{read.contract.version}</code>
          </dd>
        </div>
      </dl>

      {read.branding_version === 0 ? <Empty>{read.note}</Empty> : null}
      {read.publish_note === '' ? null : <p className="muted">“{read.publish_note}”</p>}

      {read.history.length === 0 ? (
        <p className="field__hint">
          No earlier version is kept yet. The row keeps the last {read.contract.limits.history};
          anything older is a point-in-time recovery question (spec §7.5), not a button.
        </p>
      ) : (
        <div className="row-actions">
          <span className="muted">Restore:</span>
          {read.history.map((entry) => (
            <button
              key={entry.version}
              type="button"
              className="linkish"
              onClick={() => onRollback(entry.version)}
            >
              v{entry.version} ({entry.preset})
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Rung 1 — the presets, as swatches                                   */
/* ------------------------------------------------------------------ */

function Presets({
  contract,
  value,
  onPick,
}: {
  contract: ThemeContract;
  value: string;
  onPick: (preset: string) => void;
}): ReactNode {
  return (
    <fieldset className="branding__group">
      <legend>Preset</legend>
      <p className="field__hint">
        Three designed flavours. An owner picks a mood, not a token set — and a fourth is a change
        to the bundle, not to a row.
      </p>
      <div className="swatches">
        {contract.presets.map((preset) => (
          <button
            key={preset.name}
            type="button"
            aria-pressed={preset.name === value}
            className={`swatch ${preset.name === value ? 'swatch--on' : ''}`}
            onClick={() => onPick(preset.name)}
          >
            <span
              className="swatch__chip"
              style={{
                background: preset.tokens['color.paper'],
                borderColor: preset.tokens['color.line'],
                color: preset.tokens['color.ink'],
                fontFamily: preset.tokens['font.display'],
                borderRadius: preset.tokens['radius.md'],
              }}
            >
              Aa
              <i style={{ background: preset.tokens['color.accent'] }} />
            </span>
            <strong>{preset.name}</strong>
            <span className="field__hint">{preset.blurb}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/* ------------------------------------------------------------------ */
/* Site title and tagline                                              */
/* ------------------------------------------------------------------ */

function Identity({
  draft,
  fallbackName,
  onChange,
}: {
  draft: Draft;
  fallbackName: string;
  onChange: (patch: Partial<Draft>) => void;
}): ReactNode {
  return (
    <fieldset className="branding__group">
      <legend>Words</legend>
      <div className="form__row">
        <label className="field field--wide">
          <span className="field__label">Site title</span>
          <input
            value={draft.site_title}
            placeholder={fallbackName}
            onChange={(e) => onChange({ site_title: e.target.value })}
          />
          <span className="field__hint">
            Left empty, the site uses the tenant’s own name — so a page with no branding row still
            says who it is.
          </span>
        </label>
        <label className="field field--wide">
          <span className="field__label">Tagline</span>
          <input value={draft.tagline} onChange={(e) => onChange({ tagline: e.target.value })} />
        </label>
      </div>
    </fieldset>
  );
}

/* ------------------------------------------------------------------ */
/* Rung 2 — token overrides, grouped                                   */
/* ------------------------------------------------------------------ */

function Tokens({
  contract,
  preset,
  overrides,
  onChange,
}: {
  contract: ThemeContract;
  preset: string;
  overrides: Record<string, string>;
  onChange: (tokens: Record<string, string>) => void;
}): ReactNode {
  const base = contract.presets.find((p) => p.name === preset)?.tokens ?? {};

  const set = (name: string, value: string): void => {
    const next = { ...overrides };
    if (value === '' || value === base[name]) delete next[name];
    else next[name] = value;
    onChange(next);
  };

  return (
    <fieldset className="branding__group">
      <legend>Tokens</legend>
      <p className="field__hint">
        {contract.tokens.length} names, and a {contract.tokens.length + 1}th is a contract change. A
        value left blank is the preset’s; a value identical to the preset’s is not an override and
        is not stored.
      </p>
      {contract.token_groups.map((group) => {
        const tokens = contract.tokens.filter((t) => t.group === group.key);
        if (tokens.length === 0) return null;
        const changed = tokens.filter((t) => overrides[t.name] !== undefined).length;
        return (
          <details key={group.key} className="branding__drawer">
            <summary>
              {group.label}
              {changed === 0 ? null : <span className="flag">{changed} changed</span>}
            </summary>
            <div className="form__row">
              {tokens.map((token) => (
                <TokenField
                  key={token.name}
                  token={token}
                  preset={base[token.name] ?? ''}
                  value={overrides[token.name] ?? ''}
                  onChange={(value) => set(token.name, value)}
                />
              ))}
            </div>
          </details>
        );
      })}
    </fieldset>
  );
}

/**
 * One token's input, chosen by the **type the server sent**.
 *
 * A colour gets a colour well beside its text box rather than instead of it: the
 * contract admits `rgb()`, `hsl()`, `oklch()` and `color-mix()`, and a native
 * picker can only produce hex — so the well is a convenience and the text field
 * stays the real control.
 */
function TokenField({
  token,
  preset,
  value,
  onChange,
}: {
  token: ContractToken;
  preset: string;
  value: string;
  onChange: (value: string) => void;
}): ReactNode {
  const effective = value === '' ? preset : value;

  // A `div` rather than a `label`: a colour token has two controls (the well and
  // the text box) and one label cannot name two of them. Each carries its own
  // `aria-label` instead, which is what a screen reader — and a test — reads.
  return (
    <div className="field">
      <span className="field__label">{token.label}</span>
      <span className="token-input">
        {token.type === 'color' ? (
          <input
            type="color"
            aria-label={`${token.label} colour picker`}
            value={/^#[0-9a-f]{6}$/i.test(effective) ? effective : '#000000'}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : null}
        <input
          type={token.type === 'number' ? 'number' : 'text'}
          aria-label={token.label}
          value={value}
          placeholder={preset}
          onChange={(e) => onChange(e.target.value)}
        />
      </span>
      <span className="field__hint">
        <code>{token.name}</code> — {token.hint}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Rung 3 and 4 — the arrangement and its slots                        */
/* ------------------------------------------------------------------ */

function Arrangement({
  contract,
  rows,
  zones,
  onChange,
}: {
  contract: ThemeContract;
  rows: SectionRow[];
  zones: ZoneReport[];
  onChange: (rows: SectionRow[]) => void;
}): ReactNode {
  const move = (index: number, delta: number): void => {
    const next = [...rows];
    const target = index + delta;
    const a = next[index];
    const b = next[target];
    if (a === undefined || b === undefined) return;
    next[index] = b;
    next[target] = a;
    onChange(next);
  };

  const patch = (index: number, row: SectionRow): void => {
    const next = [...rows];
    next[index] = row;
    onChange(next);
  };

  const spec = (type: string): ContractSection | undefined =>
    contract.sections.find((s) => s.type === type);

  return (
    <fieldset className="branding__group">
      <legend>Blocks</legend>
      <p className="field__hint">{contract.pinned_note}</p>
      <ol className="branding__blocks">
        {rows.map((row, index) => {
          const section = spec(row.type);
          if (section === undefined) return null;
          const pinned = row.type === contract.pinned_section;
          /**
           * The index this block had in the *published* document, which is what
           * a zone report's path is keyed by (`sections[2].slots.body_html`).
           * It is not `index`: excluded blocks stay in this list and never reach
           * the document, so matching on the slot name alone would attach a
           * report to the wrong block the moment two blocks share a slot id.
           */
          const publishedIndex = rows.slice(0, index).filter((earlier) => earlier.include).length;
          return (
            <li key={row.type} className={row.include ? '' : 'is-out'}>
              <div className="branding__block-head">
                <label className="inline-field">
                  <input
                    type="checkbox"
                    aria-label={`Include ${section.label}`}
                    checked={pinned ? true : row.include}
                    disabled={pinned}
                    onChange={(e) => patch(index, { ...row, include: e.target.checked })}
                  />
                  <strong>{section.label}</strong>
                </label>
                {pinned ? <span className="flag">always mounts</span> : null}
                <span className="muted">{section.why}</span>
                <div className="row-actions">
                  <button
                    type="button"
                    className="linkish"
                    aria-label={`Move ${section.label} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="linkish"
                    aria-label={`Move ${section.label} down`}
                    disabled={index === rows.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </button>
                </div>
              </div>

              {row.include ? (
                <details className="branding__drawer">
                  <summary>Content</summary>
                  <div className="form__row">
                    {section.slots.map((slot) => (
                      <SlotField
                        key={slot.name}
                        slot={slot}
                        value={row.slots[slot.name]}
                        zone={zones.find(
                          (z) =>
                            z.path === `sections[${String(publishedIndex)}].slots.${slot.name}`,
                        )}
                        onChange={(value) =>
                          patch(index, { ...row, slots: { ...row.slots, [slot.name]: value } })
                        }
                      />
                    ))}
                  </div>
                </details>
              ) : null}
            </li>
          );
        })}
      </ol>
    </fieldset>
  );
}

function SlotField({
  slot,
  value,
  zone,
  onChange,
}: {
  slot: ContractSlot;
  value: unknown;
  zone: ZoneReport | undefined;
  onChange: (value: unknown) => void;
}): ReactNode {
  const text = typeof value === 'string' ? value : '';

  const hint = slot.hint === undefined ? null : <span className="field__hint">{slot.hint}</span>;

  switch (slot.kind) {
    case 'flag':
      return (
        <label className="inline-field">
          <input
            type="checkbox"
            aria-label={slot.label}
            checked={typeof value === 'boolean' ? value : true}
            onChange={(e) => onChange(e.target.checked)}
          />
          {slot.label}
        </label>
      );

    case 'enum':
      return (
        <label className="field">
          <span className="field__label">{slot.label}</span>
          <select aria-label={slot.label} value={text} onChange={(e) => onChange(e.target.value)}>
            <option value="">default</option>
            {(slot.options ?? []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {hint}
        </label>
      );

    case 'prose':
      return (
        <label className="field field--wide">
          <span className="field__label">{slot.label}</span>
          <textarea
            rows={3}
            aria-label={slot.label}
            value={text}
            onChange={(e) => onChange(e.target.value)}
          />
          {hint}
        </label>
      );

    case 'richtext':
      return (
        <label className="field field--wide">
          <span className="field__label">{slot.label}</span>
          {/*
           * A plain textarea, deliberately. A rich-text widget would produce
           * markup the operator never sees and then have the server take half of
           * it away, which is the confusing version of this interaction. Typed
           * HTML plus "here is what survived" is the honest one.
           */}
          <textarea
            rows={6}
            aria-label={slot.label}
            value={text}
            onChange={(e) => onChange(e.target.value)}
          />
          {hint}
          {zone === undefined ? null : <Survived zone={zone} html={text} />}
        </label>
      );

    case 'rows':
      return <RowsField slot={slot} value={value} onChange={onChange} />;

    case 'images':
      return <ImagesField slot={slot} value={value} onChange={onChange} />;

    default:
      return (
        <label className="field field--wide">
          <span className="field__label">{slot.label}</span>
          <input
            aria-label={slot.label}
            value={text}
            placeholder={slot.fallback ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
          {hint}
        </label>
      );
  }
}

/**
 * What the server kept, shown as **bytes**.
 *
 * The textarea above already holds the stored markup — the draft is re-seeded
 * from the publish response — so this box exists to name what went, and to show
 * the exact result as text rather than as a rendered page. The console owns no
 * `dangerouslySetInnerHTML`: `apps/site`'s `RichText` is the one component in
 * the system that renders a content zone, and a second one here would be a
 * second place trusting the same bytes.
 */
function Survived({ zone, html }: { zone: ZoneReport; html: string }): ReactNode {
  if (!zone.changed) {
    return <span className="ok">Published as written — the sanitizer changed nothing.</span>;
  }
  return (
    <div className="survived">
      <p className="warn">
        Sanitized on publish: {zone.removed.join(', ')} removed ({zone.before_bytes} →{' '}
        {zone.after_bytes} bytes). What is stored, and what every guest gets, is:
      </p>
      <pre className="survived__bytes">{html}</pre>
    </div>
  );
}

function RowsField({
  slot,
  value,
  onChange,
}: {
  slot: ContractSlot;
  value: unknown;
  onChange: (value: unknown) => void;
}): ReactNode {
  const rows = readRows(value);

  const patch = (index: number, next: { label: string; value: string }): void => {
    const copy = [...rows];
    copy[index] = next;
    onChange(copy);
  };

  return (
    <div className="field field--wide">
      <span className="field__label">{slot.label}</span>
      {slot.hint === undefined ? null : <span className="field__hint">{slot.hint}</span>}
      {rows.map((row, index) => (
        <div className="form__row" key={index}>
          <input
            aria-label={`${slot.label} ${String(index + 1)} label`}
            value={row.label}
            onChange={(e) => patch(index, { ...row, label: e.target.value })}
          />
          <input
            aria-label={`${slot.label} ${String(index + 1)} value`}
            value={row.value}
            onChange={(e) => patch(index, { ...row, value: e.target.value })}
          />
          <button
            type="button"
            className="linkish"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          >
            remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className="button"
        disabled={rows.length >= slot.max}
        onClick={() => onChange([...rows, { label: '', value: '' }])}
      >
        Add row
      </button>
    </div>
  );
}

function ImagesField({
  slot,
  value,
  onChange,
}: {
  slot: ContractSlot;
  value: unknown;
  onChange: (value: unknown) => void;
}): ReactNode {
  const images = readImages(value);

  const patch = (index: number, next: { src: string; alt: string; caption: string }): void => {
    const copy = [...images];
    copy[index] = next;
    onChange(copy);
  };

  return (
    <div className="field field--wide">
      <span className="field__label">{slot.label}</span>
      {slot.hint === undefined ? null : <span className="field__hint">{slot.hint}</span>}
      {images.map((image, index) => (
        <div className="form__row" key={index}>
          <input
            aria-label={`Image ${String(index + 1)} URL`}
            placeholder="https://…"
            value={image.src}
            onChange={(e) => patch(index, { ...image, src: e.target.value })}
          />
          <input
            aria-label={`Image ${String(index + 1)} alt text`}
            placeholder="alt text"
            value={image.alt}
            onChange={(e) => patch(index, { ...image, alt: e.target.value })}
          />
          <input
            aria-label={`Image ${String(index + 1)} caption`}
            placeholder="caption"
            value={image.caption}
            onChange={(e) => patch(index, { ...image, caption: e.target.value })}
          />
          <button
            type="button"
            className="linkish"
            onClick={() => onChange(images.filter((_, i) => i !== index))}
          >
            remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className="button"
        disabled={images.length >= slot.max}
        onClick={() => onChange([...images, { src: '', alt: '', caption: '' }])}
      >
        Add image
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Rung 4 — the stylesheet                                             */
/* ------------------------------------------------------------------ */

function CustomCss({
  contract,
  value,
  pinnedTo,
  onChange,
}: {
  contract: ThemeContract;
  value: string;
  pinnedTo: number | null;
  onChange: (value: string) => void;
}): ReactNode {
  const bytes = new TextEncoder().encode(value).length;
  const limit = contract.limits.custom_css_bytes;

  return (
    <fieldset className="branding__group">
      <legend>Custom stylesheet</legend>
      <p className="field__hint">
        Rung 4, and the only tenant-authored styling. Class and element selectors,{' '}
        <code>@media</code> if you need it — no <code>@import</code>, no <code>url()</code>, no ids.
        The server refuses the rest rather than editing it: a stylesheet is written by somebody who
        meant every character.
      </p>
      <label className="field field--wide">
        <span className="field__label">CSS</span>
        <textarea
          rows={6}
          aria-label="Custom stylesheet"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className={bytes > limit ? 'error-text' : 'field__hint'}>
          {bytes} / {limit} bytes
          {pinnedTo === null
            ? ''
            : ` · the published sheet is pinned to theme contract v${String(pinnedTo)}, which is the tenant’s to re-verify on a contract bump.`}
        </span>
      </label>
      <p className="field__hint">
        The guest bundle does not inject this yet — it is stored, validated and served in{' '}
        <code>/api/site</code> ahead of the bundle learning to consume it.
      </p>
    </fieldset>
  );
}

/* ------------------------------------------------------------------ */
/* The rules, from the server                                          */
/* ------------------------------------------------------------------ */

function Sanitizer({
  contract,
  zones,
}: {
  contract: ThemeContract;
  zones: ZoneReport[];
}): ReactNode {
  const touched = zones.filter((z) => z.changed);
  return (
    <details className="branding__drawer">
      <summary>
        What a content zone may contain
        {touched.length === 0 ? null : (
          <span className="flag flag--bad">{touched.length} sanitized on last publish</span>
        )}
      </summary>
      <p className="field__hint">{contract.sanitizer.note}</p>
      <p>
        Allowed:{' '}
        {contract.sanitizer.allowed_tags.map((tag) => (
          <code key={tag}>{`<${tag}> `}</code>
        ))}
      </p>
      <p className="field__hint">
        Links may be {contract.sanitizer.allowed_link_schemes.join(' or ')} only.
      </p>
    </details>
  );
}

/* ------------------------------------------------------------------ */
/* Draft ↔ document                                                    */
/* ------------------------------------------------------------------ */

/**
 * Build the working draft from a stored document plus the contract.
 *
 * Every block in the registry gets a row: the ones the document arranged, in the
 * document's order and ticked, then the rest in the contract's order, unticked.
 * That is what makes "order + include/exclude" one list rather than two.
 *
 * A tenant who has published nothing starts on the bundle's own default
 * arrangement, so the first thing an operator sees is what the site is actually
 * showing right now — not an empty page they have to build from scratch.
 */
function fromDocument(document: BrandingDocument, contract: ThemeContract): Draft {
  const arranged =
    document.sections !== undefined && document.sections.length > 0
      ? document.sections
      : contract.default_arrangement.map((type) => ({ type, slots: {} }));

  const rows: SectionRow[] = [];
  const seen = new Set<string>();
  for (const section of arranged) {
    if (seen.has(section.type)) continue;
    if (!contract.sections.some((s) => s.type === section.type)) continue;
    seen.add(section.type);
    rows.push({ type: section.type, include: true, slots: { ...section.slots } });
  }
  for (const section of contract.sections) {
    if (seen.has(section.type)) continue;
    rows.push({ type: section.type, include: false, slots: {} });
  }

  return {
    preset: document.preset ?? contract.presets[0]?.name ?? 'serene',
    tokens: { ...(document.tokens ?? {}) },
    rows,
    site_title: document.site_title ?? '',
    tagline: document.tagline ?? '',
    custom_css: document.custom_css ?? '',
  };
}

function included(draft: Draft): BrandingSection[] {
  return draft.rows
    .filter((row) => row.include)
    .map((row) => ({ type: row.type, slots: prune(row.slots) }));
}

function toDocument(draft: Draft): BrandingDocument {
  return {
    preset: draft.preset,
    tokens: draft.tokens,
    sections: included(draft),
    ...(draft.site_title === '' ? {} : { site_title: draft.site_title }),
    ...(draft.tagline === '' ? {} : { tagline: draft.tagline }),
    ...(draft.custom_css === '' ? {} : { custom_css: draft.custom_css }),
  };
}

/** Empty strings and empty lists mean "the bundle's default"; they are not sent. */
function prune(slots: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(slots)) {
    if (value === '' || value === undefined || value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out;
}

function readRows(value: unknown): { label: string; value: string }[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    return {
      label: typeof row['label'] === 'string' ? row['label'] : '',
      value: typeof row['value'] === 'string' ? row['value'] : '',
    };
  });
}

function readImages(value: unknown): { src: string; alt: string; caption: string }[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => {
    const image = (entry ?? {}) as Record<string, unknown>;
    return {
      src: typeof image['src'] === 'string' ? image['src'] : '',
      alt: typeof image['alt'] === 'string' ? image['alt'] : '',
      caption: typeof image['caption'] === 'string' ? image['caption'] : '',
    };
  });
}
